import { CreateSecurityLaneButton } from "@/components/security/CreateSecurityLaneButton";
import { UpdateSecurityLaneDialog } from "@/components/security/UpdateSecurityLaneDialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCheckpoints,
  useDeleteSecurityLane,
  useSecurityLanes,
} from "@/queries/security";
import { AlertCircle, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

function formatLaneHours(lane: {
  start_interval: number;
  end_interval: number;
}): string {
  const startHour = Math.floor(lane.start_interval / 6);
  const startMin = (lane.start_interval % 6) * 10;

  const endDisplay =
    lane.end_interval % 144 === 0 && lane.end_interval > 0
      ? 144
      : lane.end_interval % 144;
  const endHour = endDisplay === 144 ? 24 : Math.floor(endDisplay / 6);
  const endMin = endDisplay === 144 ? 0 : (endDisplay % 6) * 10;

  const dayOffset = Math.floor(lane.end_interval / 144);
  const daySuffix =
    dayOffset > 0 && endDisplay !== 144
      ? ` (+${dayOffset} day${dayOffset > 1 ? "s" : ""})`
      : "";

  return `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")} - ${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}${daySuffix}`;
}

interface SecurityLaneItemProps {
  lane: any;
  checkpointName: string;
  onDelete: (id: number) => void;
  isDeleting: boolean;
}

const SecurityLaneItem: React.FC<SecurityLaneItemProps> = ({
  lane,
  checkpointName,
  onDelete,
  isDeleting,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const crossesMidnight = lane.crosses_midnight ?? lane.end_interval > 144;
  const formattedHours = lane.formatted_hours ?? formatLaneHours(lane);

  return (
    <>
      <div className="border rounded-md p-4 space-y-1 relative">
        <div className="absolute top-2 right-2 flex gap-2">
          <UpdateSecurityLaneDialog lane={lane} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap md:flex-nowrap items-center gap-4">
          <div className="flex-1 space-y-1">
            <h3 className="font-semibold text-lg mr-20">{lane.name}</h3>
            <p className="text-sm text-muted-foreground">
              Checkpoint: {checkpointName}
            </p>
            <p className="text-sm font-medium">{formattedHours}</p>
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm">
              <strong>Type:</strong>{" "}
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                  lane.type === "PRECHECK"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {lane.type}
              </span>
            </p>
            {crossesMidnight && (
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                Crosses midnight
              </span>
            )}
          </div>

          <div className="flex-1 space-y-1">
            <p className="text-sm text-muted-foreground">
              <strong>Created:</strong>{" "}
              {new Date(lane.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Security Lane</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{lane.name}"? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(lane.id);
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export function SecurityLanes() {
  const {
    data: lanesData,
    isLoading: isLoadingLanes,
    error: errorLanes,
  } = useSecurityLanes();

  const { data: checkpoints } = useCheckpoints();

  const deleteMutation = useDeleteSecurityLane();

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const checkpointMap = useMemo(() => {
    if (!checkpoints) return {};
    return checkpoints.reduce(
      (acc, checkpoint) => {
        acc[checkpoint.id] = checkpoint.name;
        return acc;
      },
      {} as Record<number, string>
    );
  }, [checkpoints]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Security Lanes</CardTitle>
          <CardDescription>
            Manage security checkpoint lanes and their configurations.
          </CardDescription>
        </div>
        <CreateSecurityLaneButton />
      </CardHeader>
      <CardContent>
        {isLoadingLanes && <p>Loading security lanes...</p>}
        {errorLanes && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <p>Error loading security lanes: {errorLanes.message}</p>
          </div>
        )}
        {!isLoadingLanes && !errorLanes && (
          <div className="space-y-4">
            {lanesData && lanesData.length > 0 ? (
              lanesData.map((lane) => (
                <SecurityLaneItem
                  key={lane.id}
                  lane={lane}
                  checkpointName={
                    checkpointMap[lane.checkpoint_id] ||
                    `ID: ${lane.checkpoint_id}`
                  }
                  onDelete={handleDelete}
                  isDeleting={deleteMutation.isPending}
                />
              ))
            ) : (
              <p className="text-center text-muted-foreground">
                No security lanes found. Click "Add Lane" to create one.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
